import { hasValue } from 'swipeclock-frontend';

export const CACHE_ALLOWED_FIELDS = [
  'employeeName',
  'lastPunch',
  'lastPunchUTC',
  'displayButtons',
  'disabledButtonMap',
  'enableButtons',
  'disableButtons',
  'responseType',
  'intelligentClock',
  'geoLocationRequired',
  'clockPrompts',
];

export const OFFLINE_PUNCH_ALLOWED_FIELDS = [
  'punch',
  'punchDateTime',
  'lastPunch',
  'disabledButtonMap',
  'enableButtons',
  'disableButtons',
];

export const QUEUE_ALLOWED_FIELDS = [
  'id',
  'queuedAt',
  'punchDateTime',
  'expiresAt',
  'status',
  'retryCount',
  'mobile',
  'punch',
  'source',
  'requestType',
  'clockPrompts',
  'expectedPunches',
  'geoLocation',
  'geoError',
  'overridePunch',
  'isPunchStatusOverride',
  'punchSessionId',
  'awaitingAuth',
  'retryAfter',
  'submitDisplayUntil',
  'retryDisplayUntil',
];

const PROMPT_ANSWER_FIELDS = new Set(['value', 'answer', 'response']);

const pickAllowedFields = (source, allowedFields) => {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const picked = {};

  allowedFields.forEach((field) => {
    if (field in source) {
      picked[field] = source[field];
    }
  });

  return picked;
};

const sanitizeClockPromptDefinitions = (clockPrompts) => {
  if (!Array.isArray(clockPrompts)) {
    return [];
  }

  return clockPrompts.map((prompt) => {
    if (!prompt || typeof prompt !== 'object') {
      return prompt;
    }

    const definition = { ...prompt };
    PROMPT_ANSWER_FIELDS.forEach((field) => {
      delete definition[field];
    });
    return definition;
  });
};

export const pickAllowedCacheFields = (clockConnectData) => {
  const picked = pickAllowedFields(clockConnectData, CACHE_ALLOWED_FIELDS);

  if (!picked) {
    return null;
  }

  if ('clockPrompts' in picked) {
    picked.clockPrompts = sanitizeClockPromptDefinitions(picked.clockPrompts);
  }

  if (Array.isArray(clockConnectData?.displayButtons)) {
    picked.displayButtons = clockConnectData.displayButtons;
  } else if (!Array.isArray(picked.displayButtons)) {
    picked.displayButtons = [];
  }

  if (
    clockConnectData?.disabledButtonMap &&
    typeof clockConnectData.disabledButtonMap === 'object'
  ) {
    picked.disabledButtonMap = clockConnectData.disabledButtonMap;
  } else if (
    !picked.disabledButtonMap ||
    typeof picked.disabledButtonMap !== 'object'
  ) {
    picked.disabledButtonMap = {};
  }

  return picked;
};

export const pickAllowedOfflinePunchFields = (offlinePunch) => {
  const picked = pickAllowedFields(offlinePunch, OFFLINE_PUNCH_ALLOWED_FIELDS);

  if (!picked || !hasValue(picked.punchDateTime)) {
    return null;
  }

  if (
    !picked.disabledButtonMap ||
    typeof picked.disabledButtonMap !== 'object'
  ) {
    picked.disabledButtonMap = {};
  }

  if (!Array.isArray(picked.enableButtons)) {
    picked.enableButtons = [];
  }

  if (!Array.isArray(picked.disableButtons)) {
    picked.disableButtons = [];
  }

  return picked;
};

export const pickAllowedQueueFields = (payload) => {
  const picked = pickAllowedFields(payload, QUEUE_ALLOWED_FIELDS);

  if (!picked) {
    return {};
  }

  if (picked.source === 'prompts' && hasValue(payload?.clockPrompts)) {
    picked.clockPrompts = payload.clockPrompts;
  }

  return picked;
};

export const stripEphemeralQueueFields = (item) => {
  if (!item || typeof item !== 'object') {
    return {};
  }

  const { lastError, ...persisted } = item;
  return persisted;
};

const QUEUE_ALLOWED_FIELD_SET = new Set(QUEUE_ALLOWED_FIELDS);

const clockPromptsNeedSanitizedRewrite = (clockPrompts) => {
  if (!Array.isArray(clockPrompts)) {
    return false;
  }

  return clockPrompts.some(
    (prompt) =>
      prompt &&
      typeof prompt === 'object' &&
      [...PROMPT_ANSWER_FIELDS].some((field) => field in prompt)
  );
};

/**
 * True when a queue item has disallowed top-level keys, ephemeral fields,
 * or clockPrompt answer fields that sanitize would strip — without JSON.stringify.
 */
export const queueItemNeedsSanitizedRewrite = (item) => {
  if (!item || typeof item !== 'object') {
    return false;
  }

  if ('lastError' in item) {
    return true;
  }

  const keys = Object.keys(item);

  if (keys.some((key) => !QUEUE_ALLOWED_FIELD_SET.has(key))) {
    return true;
  }

  return clockPromptsNeedSanitizedRewrite(item.clockPrompts);
};
